import { useMutation } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { AuthLayout, AuthNotice } from '../components/AuthLayout.js'
import { Button, Field, Input, PasswordInput } from '../components/ui.js'
import { useSession } from '../lib/session.js'
import { errorMessage, fieldErrors, setBearerToken, useTRPC } from '../lib/trpc.js'

export function Login() {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, refresh } = useSession()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const next = searchParams.get('next') ?? '/'

  // Why you are here rather than where you were going. `next` is set by the
  // shell's redirect, `reset` by the last step of a password reset — which
  // used to arrive at a form with no acknowledgement that anything had
  // happened, so the only way to tell it had worked was for it to work.
  const redirected = searchParams.has('next')
  const passwordWasReset = searchParams.get('reset') === '1'

  const login = useMutation(
    trpc.auth.login.mutationOptions({
      onSuccess: async (result) => {
        // Stored only for the native build, which cannot use a cookie. On the
        // web this is redundant and harmless.
        setBearerToken(result.token)
        await refresh()
        navigate(next, { replace: true })
      },
    }),
  )

  if (user) return <Navigate to={next} replace />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    login.mutate({ email, password, deviceName: deviceName() })
  }

  const errors = fieldErrors(login.error)

  return (
    <AuthLayout title="Sign in" subtitle="Welcome back. Pick up where the day left off.">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {passwordWasReset ? (
          <AuthNotice tone="good" icon="check">
            Password changed. Sign in with the new one.
          </AuthNotice>
        ) : redirected ? (
          <AuthNotice icon="shield">Sign in to open the page you were sent to.</AuthNotice>
        ) : null}

        <Field label="Email" htmlFor="email" error={errors['email']} required>
          <Input
            id="email"
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            autoFocus={focusOnLoad()}
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          error={errors['password']}
          required
          action={
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-brand-600 hover:text-brand-700"
            >
              Forgotten it?
            </Link>
          }
        >
          <PasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        {login.error ? (
          <AuthNotice tone="error" icon="alert">
            {errorMessage(login.error)}
          </AuthNotice>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          loading={login.isPending}
          className="mt-1"
        >
          Sign in
        </Button>
      </form>

      <p className="mt-8 border-t border-ink-200 pt-4 text-xs leading-snug text-ink-500">
        Accounts are created by the office. If nobody has sent you an invitation, ask them for one —
        there is nothing to sign up for here.
      </p>
    </AuthLayout>
  )
}

/**
 * Whether to put the cursor in the email box on arrival.
 *
 * Only where there is a real keyboard. Autofocus on a phone raises the soft
 * keyboard over the form before anybody has decided to type, and on the native
 * build it does it over the splash.
 */
function focusOnLoad(): boolean {
  return window.matchMedia?.('(pointer: fine)').matches ?? false
}

/**
 * Names the session after the device.
 *
 * Coarse on purpose: enough for someone to recognise the phone they lost in
 * a list of active sessions, without parsing a user-agent string into a
 * fiction about browser versions.
 */
function deviceName(): string {
  const ua = navigator.userAgent
  if (/iPhone|iPad/.test(ua)) return 'iPhone or iPad'
  if (/Android/.test(ua)) return 'Android device'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows PC'
  return 'Browser'
}
